import { useEffect, useState } from "react";
import { Truck } from "lucide-react";
import api, { formatErr } from "@/lib/api";
import { TemplateEditor } from "@/pages/ChecklistTemplates";

/**
 * Super-Admin editor for the GLOBAL Daily Vehicle Cleanliness checklists.
 * Two variants — Delivery Truck and Cold Truck — presented as tabs so
 * admins can extend each independently (e.g. add temperature-probe /
 * defrost residue checks only to the cold truck list). Reuses the
 * TemplateEditor so behaviour, styling and validation match every other
 * checklist page pixel-for-pixel.
 */
const TABS = [
  { code: "VEHICLE_CLEAN_DELIVERY", label: "Delivery Truck" },
  { code: "VEHICLE_CLEAN_COLD",     label: "Cold Truck" },
];

export default function AdminVehicleCleanChecklist() {
  const [templates, setTemplates] = useState([]);   // both master docs, keyed by code
  const [tab, setTab] = useState(TABS[0].code);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/checklist-templates");
      const all = data.templates || [];
      const filtered = TABS.map((t) => all.find((x) => x.code === t.code)).filter(Boolean);
      if (filtered.length !== TABS.length) {
        setErr("One or both Vehicle Cleanliness templates are missing — the server should auto-seed them on the next backend restart.");
      }
      setTemplates(filtered);
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const active = templates.find((t) => t.code === tab);

  return (
    <div className="vb-fade-in" data-testid="admin-vehicle-clean-page">
      <div className="flex items-center gap-3 mb-2">
        <Truck size={26} style={{ color: "var(--vb-accent)" }} />
        <h1 className="vb-heading text-3xl" style={{ color: "var(--vb-text-2)" }}>Daily Vehicle Cleanliness Checklist</h1>
      </div>
      <p className="opacity-70 mb-6 max-w-3xl">
        Manage the global bilingual checklists for Delivery Trucks and Cold Trucks. Add or rename items
        (English + 中文), deactivate items you no longer want on new forms. Deactivating never deletes history —
        past vehicle checks keep the exact wording they were captured against.
      </p>

      <div className="flex gap-2 mb-4" role="tablist" data-testid="veh-clean-tabs">
        {TABS.map((t) => (
          <button key={t.code}
            className="vb-btn sm"
            style={{
              background: tab === t.code ? "var(--vb-accent)" : "transparent",
              color: tab === t.code ? "white" : "var(--vb-text-2)",
              border: `1px solid ${tab === t.code ? "transparent" : "var(--vb-border)"}`,
            }}
            data-testid={`veh-clean-tab-${t.code}`}
            onClick={() => setTab(t.code)}>
            {t.label}
          </button>
        ))}
      </div>

      {err && <div className="vb-pill err mb-4" data-testid="admin-veh-clean-error">{err}</div>}

      {loading ? (
        <div className="opacity-60" data-testid="admin-veh-clean-loading">Loading…</div>
      ) : active ? (
        <TemplateEditor key={active.code} template={active} onSaved={load} />
      ) : null}
    </div>
  );
}
